import { NormalizedTagGroup } from 'admin/actions/user-tag-group';
import FA from 'admin/components/atoms/FontAwesome';
import Select from 'admin/components/atoms/Select';
import {
  buildItemSelectOptions,
  filterItemOption,
  formatItemOptionLabel,
  ItemOption,
} from 'admin/components/organisms/tagSelectOptions';
import React from 'react';
import styled from 'styled-components';
import { Button } from '../atoms/Buttons';

// 「グループを選択 → ラベル(item)を選択」を行単位で管理する付与情報。
export type TagSelection = { groupId: string | null; itemIds: string[] };

type Props = {
  groups: NormalizedTagGroup[];
  value: TagSelection[];
  onChange: (next: TagSelection[]) => void;
  disabled?: boolean;
  // 必須グループのid。これらは常時1行表示し、グループ変更・行削除を不可にして「*」を表示する。
  requiredGroupIds?: string[];
  // Rendered between the rows and the add button.
  errorMessage?: React.ReactNode;
  groupPlaceholder?: string;
  // Overrides both the "pick a label" and the "pick a group first" placeholders.
  itemPlaceholder?: string;
  // Ignore max_selections: allow multi-select and drop the per-group cap.
  forceMulti?: boolean;
};

// 親が flex column のときに全幅へストレッチしないよう、左寄せのコンテナで内容幅に収める。
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const GroupSelectWrapper = styled.div`
  width: 240px;
  flex-shrink: 0;
`;

const ItemSelectWrapper = styled.div`
  width: 320px;
  flex-shrink: 0;
`;

// 編集中の状態から行を取り除くだけ（DB削除ではない）ため、控えめな × アイコンにする。
const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #999;
  font-size: 16px;
  line-height: 1;
  padding: 4px 8px;
  cursor: pointer;
  &:hover {
    color: #555;
  }
  &:disabled {
    color: #ccc;
    cursor: default;
  }
`;

// 必須グループ行では削除ボタンの代わりに必須マーク（赤の「* 必須入力」）を置く。
const RequiredMark = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  color: red;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
`;

const EmptyText = styled.div`
  color: #999;
`;

// 店舗ラベルの付与UI。発行体orgの有効グループから「追加 → ラベル選択」する。
// max_selections=1 は単一選択、それ以外（0=無制限含む）は複数選択。
// requiredGroupIds に指定したグループは常時表示・変更/削除不可・必須マーク付き。
const TagSelectionEditor = ({
  groups,
  value,
  onChange,
  disabled = false,
  requiredGroupIds = [],
  errorMessage,
  groupPlaceholder = 'グループを選択',
  itemPlaceholder,
  forceMulti = false,
}: Props) => {
  if (groups.length === 0) {
    return (
      <Container>
        <EmptyText>選択可能な店舗ラベルがありません</EmptyText>
        {errorMessage}
      </Container>
    );
  }

  const requiredSet = new Set(requiredGroupIds);
  const isEmptyRequiredRow = (r: TagSelection) =>
    r.groupId !== null && requiredSet.has(r.groupId) && r.itemIds.length === 0;
  // 必須グループは未選択でも常に行を表示する（value に無ければ空行を補う）。
  // 閲覧専用(disabled)時は誤解を招くため補完しない。
  const valueGroupIds = new Set(value.map((r) => r.groupId).filter(Boolean) as string[]);
  const missingRequiredRows: TagSelection[] = disabled
    ? []
    : requiredGroupIds
        .filter((id) => !valueGroupIds.has(id) && groups.some((g) => g.id === id))
        .map((id) => ({ groupId: id, itemIds: [] }));
  const rows = [...value, ...missingRequiredRows];

  // 補完した「空の必須行」は表示専用。value(=保存状態)へ焼き込まないよう除去してから emit する
  // （未選択の必須行は描画時に毎回再補完されるため、表示は保たれる）。
  const emit = (next: TagSelection[]) => onChange(next.filter((r) => !isEmptyRequiredRow(r)));
  const removeRow = (index: number) => emit(rows.filter((_, i) => i !== index));
  const setRowGroup = (index: number, groupId: string | null) =>
    emit(rows.map((row, i) => (i === index ? { groupId, itemIds: [] } : row)));
  const setRowItems = (index: number, itemIds: string[]) =>
    emit(rows.map((row, i) => (i === index ? { ...row, itemIds } : row)));
  const addRow = () => emit([...rows, { groupId: null, itemIds: [] }]);

  return (
    <Container>
      {rows.map((row, index) => {
        const isRequired = !!row.groupId && requiredSet.has(row.groupId);
        const usedGroupIds = rows
          .filter((_, i) => i !== index)
          .map((r) => r.groupId)
          .filter(Boolean) as string[];
        const groupOptions = groups
          .filter((g) => !usedGroupIds.includes(g.id))
          .map((g) => ({ label: g.name, value: g.id }));
        const selectedGroup = groups.find((g) => g.id === row.groupId) || null;
        // subgroup がある場合は見出し付き options、無ければフラット。value 照合には flat を使う。
        // NormalizedTagItem は既に {id,name,subgroupId,subgroupName} を持つのでそのまま渡す。
        const { options: itemOptions, flat: flatItemOptions } = selectedGroup
          ? buildItemSelectOptions(selectedGroup.items, selectedGroup.subgroups)
          : { options: [] as ItemOption[], flat: [] as ItemOption[] };
        const hasSubgroups = !!selectedGroup && selectedGroup.subgroups.length > 0;
        const isMulti = !!selectedGroup && (forceMulti || selectedGroup.maxSelections !== 1);
        return (
          // 選択済みグループは groupId（重複なし）を key にし、未選択行のみ index にフォールバック
          // eslint-disable-next-line react/no-array-index-key
          <Row key={row.groupId ?? `unset-${index}`}>
            <GroupSelectWrapper>
              <Select
                options={groupOptions}
                value={
                  selectedGroup ? { label: selectedGroup.name, value: selectedGroup.id } : null
                }
                onChange={(opt: { value: string } | null) =>
                  setRowGroup(index, opt ? opt.value : null)
                }
                placeholder={groupPlaceholder}
                // 必須グループはグループ自体を変更できない
                isDisabled={disabled || isRequired}
                fullWidth
              />
            </GroupSelectWrapper>
            <ItemSelectWrapper>
              <Select
                options={itemOptions}
                isMulti={isMulti}
                // subgroup 見出し名でも検索できるよう filterOption を拡張（フラット時は既定挙動と同等）。
                filterOption={hasSubgroups ? (filterItemOption as any) : undefined}
                // 選択値の表示に所属 subgroup を併記（フラット時は item 名のみ）。
                formatOptionLabel={hasSubgroups ? (formatItemOptionLabel as any) : undefined}
                value={
                  isMulti
                    ? flatItemOptions.filter((o) => row.itemIds.includes(o.value))
                    : flatItemOptions.find((o) => o.value === row.itemIds[0]) || null
                }
                onChange={(opt: any) =>
                  setRowItems(
                    index,
                    isMulti
                      ? (opt || []).map((o: { value: string }) => o.value)
                      : opt
                      ? [opt.value]
                      : []
                  )
                }
                // 必須グループの単一選択は空にできないようにする（複数選択は submit 時に検証）
                isClearable={!isRequired}
                isDisabled={disabled || !selectedGroup}
                isOptionDisabled={() =>
                  !forceMulti &&
                  isMulti &&
                  !!selectedGroup &&
                  selectedGroup.maxSelections > 1 &&
                  row.itemIds.length >= selectedGroup.maxSelections
                }
                placeholder={
                  itemPlaceholder ?? (selectedGroup ? 'ラベルを選択' : 'グループを選択してください')
                }
                fullWidth
              />
            </ItemSelectWrapper>
            {isRequired ? (
              <RequiredMark title="必須入力">* 必須入力</RequiredMark>
            ) : (
              <RemoveButton
                type="button"
                onClick={() => removeRow(index)}
                disabled={disabled}
                title="このラベルを閉じる"
                aria-label="このラベルを閉じる"
              >
                <FA icon="times" />
              </RemoveButton>
            )}
          </Row>
        );
      })}
      {errorMessage}
      {rows.length < groups.length && (
        <Button type="button" onClick={addRow} variant="outline" disabled={disabled}>
          ＋ ラベルを追加
        </Button>
      )}
    </Container>
  );
};

export default TagSelectionEditor;
